<template>
	<v-dialog
		v-model="visible"
		width="640"
		:fullscreen="$vuetify.breakpoint.xsOnly"
		scrollable
		persistent>
		<v-card class="background-2 round-card">
			<v-card-title class="pa-3">
				<v-icon color="primary" class="mr-2">help</v-icon>
				<span class="fs16 fw500">Richiedi assistenza</span>
				<v-spacer></v-spacer>
				<v-btn icon @click="close()">
					<v-icon>close</v-icon>
				</v-btn>
			</v-card-title>
			<v-divider></v-divider>

			<v-card-text class="pa-0">
				<v-container>
					<v-row class="px-2 pt-4">
						<v-col class="px-4 py-0" cols="12">
							<v-select
								v-model="reason"
								:items="reasons"
								:loading="loadingInfo"
								:error-messages="fieldErrors.reason"
								outlined dense
								:menu-props="{'offset-y': true}"
								label="Motivo della richiesta">
							</v-select>
						</v-col>

						<v-col class="px-4 py-0" cols="12">
							<v-textarea
								v-model="message"
								:error-messages="fieldErrors.message"
								outlined
								rows="6"
								counter="10000"
								label="Descrivi la richiesta"
								hint="Più dettagli ci dai (cosa stavi facendo, cosa ti aspettavi), prima riusciamo ad aiutarti"
								persistent-hint>
							</v-textarea>
						</v-col>

						<v-col class="px-4 pt-4 pb-0" cols="12">
							<v-btn
								small outlined color="primary"
								class="mr-2 mb-2"
								:loading="capturing"
								@click="attachScreenshot()">
								<v-icon left small>mdi-monitor-screenshot</v-icon>
								Allega schermata
							</v-btn>

							<v-btn
								small outlined
								class="mb-2"
								@click="$refs.fileInput.click()">
								<v-icon left small>mdi-paperclip</v-icon>
								Allega file
							</v-btn>

							<input
								ref="fileInput"
								type="file"
								multiple
								class="d-none"
								@change="addFiles($event.target.files)">

							<div class="fs11 grey--text mb-2" :title="'Estensioni consentite: ' + allowedExtensions.join(', ')">
								Puoi anche incollare un'immagine con Ctrl+V. Immagini, PDF, documenti Office, archivi
								e video, fino a {{ humanSize(maxFileSize) }} per file.
							</div>

							<div v-if="fileError" class="error--text fs12 mb-2">{{ fileError }}</div>

							<v-chip
								v-for="(file, i) in files"
								:key="i"
								class="mr-2 mb-2"
								close
								small
								@click:close="files.splice(i, 1)">
								<v-icon left small>{{ isImage(file) ? 'mdi-image' : 'mdi-file' }}</v-icon>
								{{ file.name }} ({{ humanSize(file.size) }})
							</v-chip>
						</v-col>

						<v-col class="px-4 pt-2 pb-4" cols="12">
							<div class="fs11 grey--text">
								Alla richiesta alleghiamo in automatico la pagina da cui scrivi e i tuoi dati di
								accesso. Ti risponderemo all'indirizzo {{ userEmail }}.
							</div>
						</v-col>
					</v-row>
				</v-container>
			</v-card-text>

			<v-divider></v-divider>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text color="red" :disabled="sending" @click="close()">annulla</v-btn>
				<v-btn text color="primary" :loading="sending" @click="send()">invia</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>

	export default {
		name: 'support-request-dialog',

		props: {
			open: Boolean,
		},

		data() {
			return {
				reason: null,
				message: '',
				files: [],
				reasons: [],
				allowedExtensions: [],
				maxFileSize: 20971520,
				maxTotalSize: 41943040,
				loadingInfo: false,
				infoLoaded: false,
				sending: false,
				capturing: false,
				fileError: null,
				// Non si puo' chiamare 'errors': vee-validate espone gia' una
				// computed con quel nome in ogni componente.
				fieldErrors: {reason: null, message: null},
			}
		},

		computed: {
			// La v-dialog non puo' scrivere sulla prop: l'apertura la governa
			// App.vue, la chiusura passa sempre da close().
			visible: {
				get() {
					// Durante la cattura la finestra sparisce, altrimenti si
					// fotograferebbe se stessa al posto della pagina.
					return this.open && !this.capturing
				},
				set(value) {
					if (!value) this.close()
				},
			},

			userEmail() {
				return this.$store.getters.getProfile ? this.$store.getters.getProfile.email : ''
			},
		},

		watch: {
			open(aperto) {
				if (aperto) {
					this.loadInfo()
					document.addEventListener('paste', this.onPaste)
				} else {
					document.removeEventListener('paste', this.onPaste)
				}
			},
		},

		beforeDestroy() {
			document.removeEventListener('paste', this.onPaste)
		},

		methods: {

			loadInfo() {
				if (this.infoLoaded) return

				this.loadingInfo = true
				this.axios.get('supportrequests-info').then(response => {
					this.reasons = response.data.reasons
					this.allowedExtensions = response.data.allowed_extensions
					this.maxFileSize = response.data.max_file_size
					this.maxTotalSize = response.data.max_total_size
					this.infoLoaded = true
					this.loadingInfo = false
				}, () => {
					this.loadingInfo = false
				})
			},

			/**
			 * html2canvas ridisegna il DOM su una canvas: arriva col dialog
			 * (import dinamico, non pesa sul bundle iniziale) e non e' fedele
			 * al pixel su mappe e grafici WebGL. Per quei casi resta l'incolla
			 * o l'allegato manuale.
			 */
			async attachScreenshot() {
				this.capturing = true
				this.fileError = null

				try {
					const html2canvas = (await import('html2canvas')).default

					// Un giro di eventi perche' il dialog sparisca davvero
					// prima dello scatto.
					await this.$nextTick()
					await new Promise(resolve => setTimeout(resolve, 250))

					const canvas = await html2canvas(document.body, {
						logging: false,
						useCORS: true,
						scale: 1,
						x: window.scrollX,
						y: window.scrollY,
						width: window.innerWidth,
						height: window.innerHeight,
					})

					// toBlob risolve con null se la canvas non e' esportabile (contenuti
					// cross-origin che la "sporcano", limiti del browser): senza questo
					// controllo si allegherebbe un file rotto senza accorgersene.
					const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
					if (!blob) throw new Error('canvas non esportabile')

					const nome = 'schermata-' + window.moment().format('YYYYMMDD-HHmmss') + '.png'

					this.addFiles([new File([blob], nome, {type: 'image/png'})])
				} catch (error) {
					this.fileError = 'Non è stato possibile catturare la schermata: allega un\'immagine manualmente'
				} finally {
					this.capturing = false
				}
			},

			onPaste(event) {
				const items = (event.clipboardData || {}).items || []
				const immagini = []

				for (const item of items) {
					if (item.kind === 'file' && item.type.startsWith('image/')) {
						const file = item.getAsFile()
						if (file) {
							const estensione = (file.type.split('/')[1] || 'png').toLowerCase()
							immagini.push(new File(
								[file],
								'incollata-' + window.moment().format('YYYYMMDD-HHmmss') + '.' + estensione,
								{type: file.type}
							))
						}
					}
				}

				if (immagini.length) {
					event.preventDefault()
					this.addFiles(immagini)
				}
			},

			addFiles(elenco) {
				this.fileError = null

				for (const file of Array.from(elenco || [])) {
					const estensione = (file.name.split('.').pop() || '').toLowerCase()

					if (this.allowedExtensions.length && !this.allowedExtensions.includes(estensione)) {
						this.fileError = 'Tipo di file non consentito: ' + file.name
						continue
					}

					if (file.size > this.maxFileSize) {
						this.fileError = file.name + ' supera ' + this.humanSize(this.maxFileSize)
						continue
					}

					const totale = this.files.reduce((somma, f) => somma + f.size, file.size)
					if (totale > this.maxTotalSize) {
						this.fileError = 'Gli allegati superano ' + this.humanSize(this.maxTotalSize) + ' in totale'
						continue
					}

					this.files.push(file)
				}

				// Senza reset lo stesso file non rilancia il change.
				if (this.$refs.fileInput) this.$refs.fileInput.value = ''
			},

			send() {
				this.fieldErrors = {
					reason: this.reason ? null : 'Scegli un motivo',
					message: (this.message || '').trim() ? null : 'Descrivi la richiesta',
				}

				if (this.fieldErrors.reason || this.fieldErrors.message) return

				const formData = new FormData()
				formData.append('reason', this.reason)
				formData.append('message', this.message)
				formData.append('context[url]', window.location.href)
				formData.append('context[route]', this.$route ? this.$route.name || '' : '')
				formData.append('context[platform]', this.$capacitor ? this.$capacitor.platform : 'web')
				formData.append('context[viewport]', window.innerWidth + 'x' + window.innerHeight)
				this.files.forEach(file => formData.append('files[]', file, file.name))

				this.sending = true
				this.axios.post('supportrequests', formData, {
					headers: {'Content-Type': 'multipart/form-data', 'Accept': 'application/json'},
				}).then(() => {
					this.sending = false
					this.$store.commit('setSnackbar', {
						timeout: 6000,
						text: 'Richiesta inviata: ti risponderemo via email',
						value: true,
						color: 'success',
					})
					this.close()
				}, error => {
					this.sending = false
					const risposta = (error.response || {}).data || {}
					this.$store.commit('setSnackbar', {
						timeout: 8000,
						text: risposta.message || 'Non è stato possibile inviare la richiesta: riprova',
						value: true,
						color: 'error',
					})
				})
			},

			/**
			 * Azzera sempre: il pulsante si chiama "annulla", e riaprendo il
			 * widget con dentro il testo e (soprattutto) lo screenshot di
			 * un'altra pagina si finirebbe per mandare una richiesta che parla
			 * d'altro.
			 */
			close() {
				this.reason = null
				this.message = ''
				this.files = []
				this.fileError = null
				this.fieldErrors = {reason: null, message: null}

				this.$emit('close')
			},

			isImage(file) {
				return (file.type || '').startsWith('image/')
			},

			humanSize(bytes) {
				if (bytes >= 1048576) return Math.round(bytes / 1048576) + ' MB'
				return Math.max(1, Math.round(bytes / 1024)) + ' KB'
			},
		},
	}

</script>
